import { useQuery } from "@tanstack/react-query";
import { flattenAssetTree } from "../components/snowflakes/assets/assetModel";
import { displayWorkOrderNumber } from "../components/snowflakes/work-orders/presentation";
import { useWorkScope } from "../components/WorkScopeProvider";
import { canAccessWebNavigationModule, type WorkModule } from "../lib/access";
import type {
  NavigationOrigin,
  NavigationOriginType,
} from "../lib/navigationOrigin";
import type { WorkOrderId } from "../lib/workIds";
import { useAssetTreeQuery } from "./useAssetTreeQuery";
import { fetchWorkOrderDetail } from "./useWorkOrderDetailQuery";
import { useWorkRequestQuery } from "./useWorkRequestQuery";

export type NavigationOriginLabel =
  | { status: "pending"; label?: undefined }
  | { status: "resolved"; label: string }
  | { status: "unavailable"; label?: undefined };

const ORIGIN_MODULE: Record<NavigationOriginType, WorkModule> = {
  asset: "ASSETS",
  request: "REQUESTS",
  workOrder: "WORK_ORDERS",
};

/**
 * Resolves the display label for a back link's origin record (for example
 * "WR-123", "WO-45 · Replace hydraulic hose" or an asset name). The label always comes from the API,
 * never from the URL. An origin the viewer may not open, or one that is
 * missing, deleted or in another facility, is "unavailable" so the caller
 * falls back to the page's normal back link; none of these show a toast.
 */
export function useNavigationOriginLabelQuery(
  origin: NavigationOrigin | undefined,
): NavigationOriginLabel {
  const { selectedFacility } = useWorkScope();
  const canOpenOrigin = Boolean(
    origin &&
      selectedFacility &&
      canAccessWebNavigationModule(
        selectedFacility,
        ORIGIN_MODULE[origin.type],
      ),
  );
  const workOrder = useOriginWorkOrderQuery(
    canOpenOrigin && origin?.type === "workOrder" ? origin.id : undefined,
  );
  const request = useWorkRequestQuery(
    canOpenOrigin && origin?.type === "request" ? origin.id : undefined,
  );
  const assetTree = useAssetTreeQuery({
    enabled: canOpenOrigin && origin?.type === "asset",
  });

  if (!origin || !canOpenOrigin) return { status: "unavailable" };
  switch (origin.type) {
    case "workOrder":
      return toLabel(
        workOrder.status,
        workOrder.data && `${workOrder.data.number} · ${workOrder.data.title}`,
      );
    case "request":
      return toLabel(request.status, request.data?.requestNumber);
    case "asset": {
      if (assetTree.status !== "success") return toLabel(assetTree.status);
      const asset = flattenAssetTree(assetTree.data.nodes).find(
        (row) => row.assetId === origin.id,
      );
      return toLabel("success", asset?.name);
    }
  }
}

function toLabel(
  status: "pending" | "error" | "success",
  label?: string,
): NavigationOriginLabel {
  if (status === "pending") return { status: "pending" };
  return status === "success" && label
    ? { status: "resolved", label }
    : { status: "unavailable" };
}

/**
 * Separate from `useWorkOrderDetailQuery` on purpose: that query toasts on
 * failure and polls while the detail page is open, but a missing origin must
 * fall back silently and a label never needs refreshing.
 */
function useOriginWorkOrderQuery(workOrderId: WorkOrderId | undefined) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const customerId = selectedCustomer?.id;
  const facilityId = selectedFacility?.id;
  return useQuery({
    queryKey: [
      "navigation-origin",
      "work-order",
      customerId,
      facilityId,
      workOrderId,
    ],
    queryFn: async () => {
      if (!customerId || !facilityId || !workOrderId) {
        throw new Error(
          "A customer, facility, and Work Order must be selected.",
        );
      }
      const detail = await fetchWorkOrderDetail(
        customerId,
        facilityId,
        workOrderId,
      );
      return {
        number: displayWorkOrderNumber(detail.workOrder.number),
        title: detail.workOrder.title,
      };
    },
    enabled: Boolean(customerId && facilityId && workOrderId),
    retry: false,
  });
}
